import { useState, Dispatch } from 'react';
import { AxiosResponse } from 'axios';
import { XUserInteraction } from '@core/utils';

export type PopupMode = 'add' | 'copy';

export interface RowData {
  Id: string | number | undefined;
  mode?: PopupMode;
  [key: string]: unknown;
}

type HandleSave = (customData?: any) => void;

export type SendFn<TParams> = (
  params: TParams
) => Promise<AxiosResponse<unknown>>;

export interface SendAddFnParams<TData> {
  data: TData;
}

export type SendUpdateFnParams<TData> = {
  id: string | number | undefined;
} & SendAddFnParams<TData>;

export type SendUpdateFn<TData> = SendFn<SendUpdateFnParams<TData>>;

export type SendAddFn<TData> = SendFn<SendAddFnParams<TData>>;

export const useSendData = <TData>(
  data: RowData,
  formData: any,
  setShowPopup: Dispatch<boolean>,
  addFunction: SendAddFn<TData> | null | undefined,
  updateFunction?: SendUpdateFn<TData> | null | undefined,
  onSaved?: () => void
): [HandleSave, boolean] => {
  const [dataLoading, setDataLoading] = useState(false);

  const sendData = <TParams>(send: SendFn<TParams>, params: TParams) => {
    setDataLoading(true);

    send(params)
      .then(() => {
        if (onSaved) {
          onSaved();
        }

        setShowPopup(false);
      })
      .catch((err: any) => {
        XUserInteraction.notifyError(err);
        setDataLoading(false);
      });
  };

  const handleSave = (customData?: TData) => {
    let dataToUpload = {};

    if (customData) {
      dataToUpload = customData;
    } else {
      dataToUpload = { ...formData, Id: undefined, mode: undefined };
    }

    if (!data.mode && updateFunction) {
      sendData<SendUpdateFnParams<TData>>(updateFunction, {
        id: data.Id,
        data: dataToUpload as TData
      });
    } else if (addFunction) {
      sendData<SendAddFnParams<TData>>(addFunction, {
        data: dataToUpload as TData
      });
    }
  };

  return [handleSave, dataLoading];
};
